import { ClerkProvider } from "@clerk/clerk-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Redirect, Route, Switch, useRoute } from "wouter";
import { AppSidebar } from "@/components/app-sidebar";
import { ErrorBoundary } from "@/components/error-boundary";
import { SplitViewPanel } from "@/components/split-view-panel";
import { ThemeProvider } from "@/components/theme-provider";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { useEmployeeAccess } from "@/hooks/use-employee-access";
import { SplitViewProvider } from "@/hooks/use-split-view";
import { useSurveyAccess } from "@/hooks/use-survey-access";
import AcceptOffer from "@/pages/accept-offer";
import ApplicantDetail from "@/pages/applicant-detail";
import Applicants from "@/pages/applicants";
import BackofficePage from "@/pages/backoffice";
import Careers from "@/pages/careers";
import ChangelogsPage from "@/pages/changelogs";
import Company from "@/pages/company";
import CompanySection from "@/pages/company-section";
import Dashboard from "@/pages/dashboard";
import DossierPage from "@/pages/dossier";
import EmployeeDashboard from "@/pages/employee-dashboard";
import EmployeeDetails from "@/pages/employee-details";
import Employees from "@/pages/employees";
import JobDetail from "@/pages/job-detail";
import Jobs from "@/pages/jobs";
import KnowledgeBase from "@/pages/knowledge-base";
import LabsCandidateIntelligence from "@/pages/labs/candidate-intelligence";
import EvaluationSettings from "@/pages/labs/evaluation-settings";
import LabsLevelAssessment from "@/pages/labs/level-assessment";
import LabsLevelAssessmentCalibrate from "@/pages/labs/level-assessment/calibrate";
import LabsLevelAssessmentCalibration from "@/pages/labs/level-assessment/calibration";
import LabsLevelAssessmentCalibrationData from "@/pages/labs/level-assessment/calibration-data";
import LabsLevelAssessmentCandidates from "@/pages/labs/level-assessment/candidates";
import LabsLevelAssessmentInterviewPanel from "@/pages/labs/level-assessment/candidates/panel";
import LabsLevelAssessmentCandidateTimeline from "@/pages/labs/level-assessment/candidates/timeline";
import LabsLevelAssessmentConfig from "@/pages/labs/level-assessment/config";
import LabsLevelAssessmentModels from "@/pages/labs/level-assessment/models";
import LabsLevelAssessmentModelsNew from "@/pages/labs/level-assessment/models/new";
import LevelAssessmentCreateWorkflow from "@/pages/labs/level-assessment/models/workflow-shell";
import LabsLevelAssessmentSets from "@/pages/labs/level-assessment/sets";
import LabsLevelAssessmentSetView from "@/pages/labs/level-assessment/sets/set-view";
import LabsLevelAssessmentShared from "@/pages/labs/level-assessment/shared";
import OfferRecommendationFeedback from "@/pages/labs/offer-recommendation-feedback";
import LabsSourcing from "@/pages/labs/sourcing";
import SourcingProjectCustomize from "@/pages/labs/sourcing/projects/project-customize";
import SourcingProjectDetail from "@/pages/labs/sourcing/projects/project-detail";
import Login from "@/pages/login";
import MyInterviews from "@/pages/my-interviews";
import MySurveyResponse from "@/pages/my-survey-response";
import MySurveys from "@/pages/my-surveys";
import NotFound from "@/pages/not-found";
import NotionSync from "@/pages/notion-sync";
import OfferAcceptPaymix from "@/pages/offer-accept-paymix";
import OfferPreview from "@/pages/offer-preview";
import OfferTemplates from "@/pages/offer-templates";
import OfferView from "@/pages/offer-view";
import Offers from "@/pages/offers";
import OnboardingCustomize from "@/pages/onboarding-customize";
import OnboardingDashboard from "@/pages/onboarding-dashboard";
import Onboardings from "@/pages/onboardings";
import OrgChart from "@/pages/org-chart";
import Performance from "@/pages/performance";
import PortalContent from "@/pages/portal/portal-content";
import PortalCsat from "@/pages/portal/portal-csat";
import PortalMilestone from "@/pages/portal/portal-milestone";
import PortalRouter from "@/pages/portal/portal-router";
import ProfilePage from "@/pages/profile";
import SalaryTableDetail from "@/pages/salary-table-detail";
import SalaryTables from "@/pages/salary-tables";
import SchedulingLinks from "@/pages/scheduling-links";
import SettingsPage from "@/pages/settings";
import SSOCallback from "@/pages/sso-callback";
import SurveyDetail from "@/pages/survey-detail";
import SurveyFormPage from "@/pages/survey-form";
import SurveyInstanceDetail from "@/pages/survey-instance-detail";
import Surveys from "@/pages/surveys";
import TemplateCanvas from "@/pages/template-canvas";
import { queryClient } from "./lib/queryClient";

function AdminRouter() {
  return (
    <Switch>
      <Route path="/" component={Dashboard} />
      <Route path="/employees" component={Employees} />
      <Route path="/employees/:id" component={EmployeeDetails} />
      <Route path="/org-chart" component={OrgChart} />
      <Route path="/jobs" component={Jobs} />
      <Route path="/jobs/:id" component={JobDetail} />
      <Route path="/applicants/:id" component={ApplicantDetail} />
      <Route path="/applicants" component={Applicants} />
      <Route path="/onboarding" component={PortalRouter} />
      <Route path="/onboarding/milestone/:day" component={PortalMilestone} />
      <Route path="/onboarding/content/:resourceId" component={PortalContent} />
      <Route path="/onboarding/csat" component={PortalCsat} />
      <Route path="/company" component={Company} />
      <Route path="/company/:resourceId" component={CompanySection} />
      <Route path="/onboardings" component={Onboardings} />
      <Route path="/onboardings/:id/customize" component={OnboardingCustomize} />
      <Route path="/onboardings/:id" component={OnboardingDashboard} />
      <Route path="/performance" component={Performance} />
      <Route path="/surveys" component={Surveys} />
      <Route path="/surveys/new" component={SurveyFormPage} />
      <Route path="/surveys/instances/:id" component={SurveyInstanceDetail} />
      <Route path="/surveys/:id/edit" component={SurveyFormPage} />
      <Route path="/surveys/:id" component={SurveyDetail} />
      <Route path="/offers" component={Offers} />
      <Route path="/offer-templates" component={OfferTemplates} />
      <Route path="/onboarding-templates">
        <Redirect to="/onboardings?tab=templates" />
      </Route>
      <Route path="/onboarding-templates/:id/canvas" component={TemplateCanvas} />
      <Route path="/admin/knowledge-base" component={KnowledgeBase} />
      <Route path="/admin/settings" component={SettingsPage} />
      <Route path="/notion-sync" component={NotionSync} />
      <Route path="/backoffice" component={BackofficePage} />
      <Route path="/admin/changelogs" component={ChangelogsPage} />
      <Route path="/profile" component={ProfilePage} />
      <Route path="/my-interviews" component={MyInterviews} />
      <Route path="/my-surveys/:instanceId" component={MySurveyResponse} />
      <Route path="/my-surveys" component={MySurveys} />
      <Route path="/dossier/:applicationId" component={DossierPage} />
      <Route path="/scheduling-links" component={SchedulingLinks} />
      <Route path="/salary-tables" component={SalaryTables} />
      <Route path="/salary-tables/:id" component={SalaryTableDetail} />
      <Route path="/labs/candidate-intelligence" component={LabsCandidateIntelligence} />
      <Route path="/labs/level-assessment/models/new" component={LevelAssessmentCreateWorkflow} />
      <Route
        path="/labs/level-assessment/models/:modelId/disciplines/new"
        component={LevelAssessmentCreateWorkflow}
      />
      <Route path="/labs/level-assessment/models/:modelId/edit">
        <LabsLevelAssessmentModelsNew />
      </Route>
      <Route path="/labs/level-assessment/models" component={LabsLevelAssessmentModels} />
      <Route path="/labs/level-assessment/config" component={LabsLevelAssessmentConfig} />
      <Route path="/labs/level-assessment/calibration" component={LabsLevelAssessmentCalibration} />
      <Route
        path="/labs/level-assessment/calibration-data"
        component={LabsLevelAssessmentCalibrationData}
      />
      <Route
        path="/labs/level-assessment/calibrate/:sessionId"
        component={LabsLevelAssessmentCalibrate}
      />
      <Route
        path="/labs/level-assessment/candidates/:candidateId/rounds/:roundId"
        component={LabsLevelAssessmentInterviewPanel}
      />
      <Route
        path="/labs/level-assessment/candidates/:candidateId"
        component={LabsLevelAssessmentCandidateTimeline}
      />
      <Route path="/labs/level-assessment/candidates" component={LabsLevelAssessmentCandidates} />
      <Route path="/labs/level-assessment/sets/:setId" component={LabsLevelAssessmentSetView} />
      <Route path="/labs/level-assessment/sets" component={LabsLevelAssessmentSets} />
      <Route path="/labs/level-assessment/e/:evaluationId" component={LabsLevelAssessmentShared} />
      <Route path="/labs/level-assessment/evaluate" component={LabsLevelAssessment} />
      <Route path="/labs/level-assessment">
        <Redirect to="/labs/level-assessment/candidates" />
      </Route>
      <Route
        path="/labs/sourcing/projects/:projectId/customize"
        component={SourcingProjectCustomize}
      />
      <Route path="/labs/sourcing/projects/:projectId" component={SourcingProjectDetail} />
      <Route path="/labs/sourcing" component={LabsSourcing} />
      <Route path="/labs/evaluation-settings" component={EvaluationSettings} />
      <Route path="/labs/offer-recommendation-feedback" component={OfferRecommendationFeedback} />
      <Route component={NotFound} />
    </Switch>
  );
}

function EmployeeRouter() {
  const { isManager, hasOnboardingTemplateAccess, isBuddyOfActiveOnboarding, hasPermission } =
    useAuth();
  const canSeeOnboarding = isManager || hasPermission("onboarding") || hasOnboardingTemplateAccess;
  // "Colaboradores" / "Operador de Colaboradores" gate the employee-management
  // pages. Managers keep access to their own team (server scopes the list to
  // direct reports). Mirrors the sidebar gate in app-sidebar.tsx.
  const { canMaintainEmployeeBase } = useEmployeeAccess();
  const canSeeEmployees = isManager || canMaintainEmployeeBase;
  // Same pairing for the survey pages: the Admin/Operador de Pesquisas reaches
  // them through their permission, the manager through the team view. Must move
  // together with the sidebar gate in app-sidebar.tsx, or the link 404s.
  const { canOperateSurveys } = useSurveyAccess();
  const canSeeSurveys = isManager || canOperateSurveys;

  return (
    <Switch>
      <Route path="/" component={EmployeeDashboard} />
      <Route path="/onboarding" component={PortalRouter} />
      <Route path="/onboarding/milestone/:day" component={PortalMilestone} />
      <Route path="/onboarding/content/:resourceId" component={PortalContent} />
      <Route path="/onboarding/csat" component={PortalCsat} />
      <Route path="/company" component={Company} />
      <Route path="/company/:resourceId" component={CompanySection} />
      <Route path="/my-interviews" component={MyInterviews} />
      <Route path="/profile" component={ProfilePage} />
      <Route path="/my-surveys/:instanceId" component={MySurveyResponse} />
      <Route path="/my-surveys" component={MySurveys} />
      <Route path="/dossier/:applicationId" component={DossierPage} />
      <Route path="/scheduling-links" component={SchedulingLinks} />
      <Route path="/jobs" component={Jobs} />
      <Route path="/jobs/:id" component={JobDetail} />
      <Route path="/org-chart" component={OrgChart} />
      {/* Labs is open to every authenticated user — non-manager testers need
       *  /candidate-intelligence to try the tool. /evaluation-settings routes
       *  through to a role-gated page that renders an access-denied card for
       *  non-managers instead of 404'ing. */}
      <Route path="/labs/candidate-intelligence" component={LabsCandidateIntelligence} />
      <Route path="/labs/level-assessment/models/new" component={LevelAssessmentCreateWorkflow} />
      <Route
        path="/labs/level-assessment/models/:modelId/disciplines/new"
        component={LevelAssessmentCreateWorkflow}
      />
      <Route path="/labs/level-assessment/models/:modelId/edit">
        <LabsLevelAssessmentModelsNew />
      </Route>
      <Route path="/labs/level-assessment/models" component={LabsLevelAssessmentModels} />
      <Route path="/labs/level-assessment/config" component={LabsLevelAssessmentConfig} />
      <Route path="/labs/level-assessment/calibration" component={LabsLevelAssessmentCalibration} />
      <Route
        path="/labs/level-assessment/calibration-data"
        component={LabsLevelAssessmentCalibrationData}
      />
      <Route
        path="/labs/level-assessment/calibrate/:sessionId"
        component={LabsLevelAssessmentCalibrate}
      />
      <Route
        path="/labs/level-assessment/candidates/:candidateId/rounds/:roundId"
        component={LabsLevelAssessmentInterviewPanel}
      />
      <Route
        path="/labs/level-assessment/candidates/:candidateId"
        component={LabsLevelAssessmentCandidateTimeline}
      />
      <Route path="/labs/level-assessment/candidates" component={LabsLevelAssessmentCandidates} />
      <Route path="/labs/level-assessment/sets/:setId" component={LabsLevelAssessmentSetView} />
      <Route path="/labs/level-assessment/sets" component={LabsLevelAssessmentSets} />
      <Route path="/labs/level-assessment/e/:evaluationId" component={LabsLevelAssessmentShared} />
      <Route path="/labs/level-assessment/evaluate" component={LabsLevelAssessment} />
      <Route path="/labs/level-assessment">
        <Redirect to="/labs/level-assessment/candidates" />
      </Route>
      <Route
        path="/labs/sourcing/projects/:projectId/customize"
        component={SourcingProjectCustomize}
      />
      <Route path="/labs/sourcing/projects/:projectId" component={SourcingProjectDetail} />
      <Route path="/labs/sourcing" component={LabsSourcing} />
      <Route path="/labs/evaluation-settings" component={EvaluationSettings} />
      <Route path="/labs/offer-recommendation-feedback" component={OfferRecommendationFeedback} />
      {(canSeeOnboarding || isBuddyOfActiveOnboarding) && (
        <>
          <Route path="/onboardings" component={Onboardings} />
          <Route path="/onboardings/:id" component={OnboardingDashboard} />
          <Route path="/onboardings/:id/customize" component={OnboardingCustomize} />
        </>
      )}
      {canSeeOnboarding && (
        <>
          <Route path="/onboarding-templates">
            <Redirect to="/onboardings?tab=templates" />
          </Route>
          <Route path="/onboarding-templates/:id/canvas" component={TemplateCanvas} />
        </>
      )}
      {canSeeEmployees && (
        <>
          <Route path="/employees/:id" component={EmployeeDetails} />
          <Route path="/employees" component={Employees} />
        </>
      )}
      {canSeeSurveys && (
        <>
          <Route path="/surveys" component={Surveys} />
          {canOperateSurveys && <Route path="/surveys/new" component={SurveyFormPage} />}
          <Route path="/surveys/instances/:id" component={SurveyInstanceDetail} />
          {canOperateSurveys && <Route path="/surveys/:id/edit" component={SurveyFormPage} />}
          <Route path="/surveys/:id" component={SurveyDetail} />
        </>
      )}
      {isManager && (
        <>
          <Route path="/applicants/:id" component={ApplicantDetail} />
          <Route path="/salary-tables" component={SalaryTables} />
          <Route path="/salary-tables/:id" component={SalaryTableDetail} />
          <Route path="/offer-templates" component={OfferTemplates} />
        </>
      )}
      <Route component={NotFound} />
    </Switch>
  );
}

function ExternalRouter() {
  return (
    <Switch>
      <Route path="/onboarding" component={PortalRouter} />
      <Route path="/onboarding/csat" component={PortalCsat} />
      <Route path="/onboarding/:rest*" component={PortalRouter} />
      <Route path="/jobs" component={Jobs} />
      <Route path="/jobs/:id" component={JobDetail} />
      <Route>
        <Redirect to="/jobs" />
      </Route>
    </Switch>
  );
}

function ImpersonationBlockedScreen() {
  const { logout } = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md text-center space-y-4 p-8">
        <h1 className="text-xl font-semibold">Impersonation não permitida</h1>
        <p className="text-muted-foreground">
          Apenas super admins podem usar o impersonate. Pare o impersonate no painel do Clerk e
          tente novamente.
        </p>
        <button
          type="button"
          onClick={logout}
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Sair e voltar ao login
        </button>
      </div>
    </div>
  );
}

function AuthenticatedApp() {
  const { isAuthenticated, isLoading, isSuperAdmin, isExternal, impersonationBlocked } = useAuth();
  const [isOfferPreview] = useRoute("/offer-preview/:token");

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (impersonationBlocked) {
    return <ImpersonationBlockedScreen />;
  }

  if (!isAuthenticated) {
    const currentPath = window.location.pathname;
    const redirectParam =
      currentPath && currentPath !== "/" && currentPath !== "/login"
        ? `?redirect=${encodeURIComponent(currentPath)}`
        : "";
    return <Redirect to={`/login${redirectParam}`} />;
  }

  // Offer preview renders full-page without sidebar
  if (isOfferPreview) {
    return <OfferPreview />;
  }

  const style = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3.5rem",
  };

  if (isExternal) {
    return (
      <div className="flex h-screen w-full">
        <main className="flex-1 overflow-auto p-6 flex flex-col">
          <ExternalRouter />
        </main>
      </div>
    );
  }

  return (
    <SplitViewProvider>
      <SidebarProvider style={style as React.CSSProperties}>
        <div className="flex h-screen w-full">
          <AppSidebar />
          <div className="flex flex-1 min-w-0">
            <main className="flex-1 overflow-auto p-6 flex flex-col">
              {isSuperAdmin ? <AdminRouter /> : <EmployeeRouter />}
            </main>
            <SplitViewPanel />
          </div>
        </div>
      </SidebarProvider>
    </SplitViewProvider>
  );
}

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

function MaybeClerkProvider({ children }: { children: React.ReactNode }) {
  if (clerkPubKey) {
    return <ClerkProvider publishableKey={clerkPubKey}>{children}</ClerkProvider>;
  }
  return <>{children}</>;
}

function App() {
  return (
    <MaybeClerkProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="light" storageKey="hr-theme">
          <TooltipProvider>
            <ErrorBoundary>
              <AuthProvider>
                <Switch>
                  <Route path="/login" component={Login} />
                  <Route path="/sso-callback" component={SSOCallback} />
                  <Route path="/careers" component={Careers} />
                  <Route path="/accept-offer/:token/paymix" component={OfferAcceptPaymix} />
                  <Route path="/accept-offer/:token" component={AcceptOffer} />
                  <Route path="/offer/:token" component={OfferView} />
                  <Route>
                    <AuthenticatedApp />
                  </Route>
                </Switch>
              </AuthProvider>
            </ErrorBoundary>
            <Toaster />
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </MaybeClerkProvider>
  );
}

export default App;
